/**
 * BalanceStateManager - Single Source of Truth para balances de cuentas
 * Arquitectura SAAS profesional con caché inteligente
 */
class BalanceStateManager {
    constructor() {
        this.balanceCache = new Map();      // Map<accountId, calculatedBalance>
        this.tradesCache = new Map();       // Map<accountId, Trade[]>
        this.totalProfitCache = new Map();  // Map<accountId, totalProfitAllTime>
        this.cashflowsCache = new Map();    // Map<accountId, Cashflow[]>
        this.accounts = null;                // Referencia a window.accounts
    }

    /**
     * Inicializar con datos de cuentas
     * @param {Object} accounts - Objeto con todas las cuentas {accountId: accountData}
     */
    initialize(accounts) {
        this.accounts = accounts;
        this.balanceCache.clear();
        this.cashflowsCache.clear();
        this.totalProfitCache.clear();
    }

    /**
     * Cargar trades de una cuenta específica en caché
     * @param {string} accountId - ID de la cuenta
     * @param {Array} trades - Array de trades de la cuenta
     */
    loadTrades(accountId, trades) {
        this.tradesCache.set(accountId, trades);
        this.invalidateBalance(accountId);
    }

    /**
     * Cargar profit total (all-time) de una cuenta
     * @param {string} accountId - ID de la cuenta
     * @param {number} totalProfit - Profit total all-time
     */
    loadTotalProfit(accountId, totalProfit) {
        const safeTotal = Number(totalProfit);
        this.totalProfitCache.set(accountId, Number.isFinite(safeTotal) ? safeTotal : 0);
        this.invalidateBalance(accountId);
    }

    /**
     * Cargar cashflows de una cuenta específica en caché
     * @param {string} accountId - ID de la cuenta
     * @param {Array} cashflows - Array de cashflows
     */
    loadCashflows(accountId, cashflows) {
        this.cashflowsCache.set(accountId, cashflows);
        this.invalidateBalance(accountId);
    }

    /**
     * Obtener balance real de una cuenta (con caché inteligente)
     * ESTE ES EL ÚNICO MÉTODO QUE DEBE USARSE PARA OBTENER BALANCE
     * @param {string} accountId - ID de la cuenta
     * @returns {number} Balance real calculado
     */
    getAccountBalance(accountId) {
        // 1. Verificar caché
        if (this.balanceCache.has(accountId)) {
            return this.balanceCache.get(accountId);
        }

        // 2. Obtener datos de la cuenta
        const account = this.accounts?.[accountId];
        if (!account) {
            return 0;
        }

        // 3. Calcular balance real
        const initialBalance = account.initial_balance || account.balance || 0;
        const cachedTotalProfit = this.totalProfitCache.get(accountId);
        const trades = this.tradesCache.get(accountId) || [];
        const totalProfit = Number.isFinite(cachedTotalProfit)
            ? cachedTotalProfit
            : trades.reduce((sum, t) => sum + (parseFloat(t.profitUSD) || 0), 0);
        const cashflows = this.cashflowsCache.get(accountId) || [];
        const totalCashflows = cashflows.reduce((sum, c) => {
            const amount = Number(c.amount) || 0;
            const isWithdraw = String(c.type || '').toLowerCase() === 'withdraw';
            return sum + (isWithdraw ? -Math.abs(amount) : Math.abs(amount));
        }, 0);
        const realBalance = initialBalance + totalProfit + totalCashflows;

        // 4. Guardar en caché
        this.balanceCache.set(accountId, realBalance);

        return realBalance;
    }

    /**
     * Agregar un nuevo trade a una cuenta
     * @param {string} accountId - ID de la cuenta
     * @param {Object} trade - Objeto trade
     */
    addTrade(accountId, trade) {
        const trades = this.tradesCache.get(accountId) || [];
        trades.push(trade);
        this.tradesCache.set(accountId, trades);
        if (this.totalProfitCache.has(accountId)) {
            const prev = this.totalProfitCache.get(accountId) || 0;
            const delta = parseFloat(trade?.profitUSD) || 0;
            this.totalProfitCache.set(accountId, prev + delta);
        }
        this.invalidateBalance(accountId);
    }

    /**
     * Agregar un nuevo cashflow a una cuenta
     * @param {string} accountId - ID de la cuenta
     * @param {Object} cashflow - Objeto cashflow
     */
    addCashflow(accountId, cashflow) {
        const list = this.cashflowsCache.get(accountId) || [];
        list.push(cashflow);
        this.cashflowsCache.set(accountId, list);
        this.invalidateBalance(accountId);
    }

    /**
     * Eliminar un cashflow de una cuenta
     * @param {string} accountId - ID de la cuenta
     * @param {string} cashflowId - ID del cashflow
     */
    removeCashflow(accountId, cashflowId) {
        const list = this.cashflowsCache.get(accountId) || [];
        const filtered = list.filter(c => c.id !== cashflowId);
        this.cashflowsCache.set(accountId, filtered);
        this.invalidateBalance(accountId);
    }

    /**
     * Eliminar un trade de una cuenta
     * @param {string} accountId - ID de la cuenta
     * @param {string} tradeId - ID del trade a eliminar
     */
    removeTrade(accountId, tradeId) {
        const trades = this.tradesCache.get(accountId) || [];
        const filtered = trades.filter(t => t.id !== tradeId);
        this.tradesCache.set(accountId, filtered);
        if (this.totalProfitCache.has(accountId)) {
            const removed = trades.find(t => t.id === tradeId);
            const delta = parseFloat(removed?.profitUSD) || 0;
            const prev = this.totalProfitCache.get(accountId) || 0;
            this.totalProfitCache.set(accountId, prev - delta);
        }
        this.invalidateBalance(accountId);
    }

    /**
     * Actualizar un trade existente
     * @param {string} accountId - ID de la cuenta
     * @param {string} tradeId - ID del trade
     * @param {Object} updatedTrade - Datos actualizados del trade
     */
    updateTrade(accountId, tradeId, updatedTrade) {
        const trades = this.tradesCache.get(accountId) || [];
        const index = trades.findIndex(t => t.id === tradeId);
        if (index !== -1) {
            if (this.totalProfitCache.has(accountId)) {
                const prevProfit = parseFloat(trades[index]?.profitUSD) || 0;
                const nextProfit = parseFloat(updatedTrade?.profitUSD ?? trades[index]?.profitUSD) || 0;
                const prevTotal = this.totalProfitCache.get(accountId) || 0;
                this.totalProfitCache.set(accountId, prevTotal - prevProfit + nextProfit);
            }
            trades[index] = { ...trades[index], ...updatedTrade };
            this.tradesCache.set(accountId, trades);
            this.invalidateBalance(accountId);
        }
    }

    /**
     * Invalidar caché de balance (forzar recálculo)
     * @param {string} accountId - ID de la cuenta
     */
    invalidateBalance(accountId) {
        this.balanceCache.delete(accountId);
    }

    /**
     * Invalidar todos los balances (útil al cambiar de cuenta)
     */
    invalidateAllBalances() {
        this.balanceCache.clear();
    }

    /**
     * Obtener todos los balances calculados (para dropdown)
     * @returns {Object} {accountId: balance}
     */
    getAllBalances() {
        if (!this.accounts) return {};

        const balances = {};
        Object.keys(this.accounts).forEach(accountId => {
            balances[accountId] = this.getAccountBalance(accountId);
        });
        return balances;
    }

    /**
     * Obtener initial_balance de una cuenta
     * @param {string} accountId - ID de la cuenta
     * @returns {number}
     */
    getInitialBalance(accountId) {
        const account = this.accounts?.[accountId];
        return account?.initial_balance || account?.balance || 0;
    }

    /**
     * Debug: Ver estado actual del manager
     */
    debug() {
        // Debug method - keep empty for backwards compatibility
    }
}

// Instancia global (Singleton)
window.balanceStateManager = new BalanceStateManager();
